import { useReducer, Dispatch } from 'react';
import {
  saveSubscriptions,
  getSubscriptions,
} from '../../../../../lib/api/subscriptions';
import { Subscription } from '../../../../../models/subscriptions';
import {
  useMountEffect,
  createPromiseDispatcher,
} from '../../../../../lib/hooks';
import { hasAccountToken } from '../../../../../lib/accountUtils';
import { getProfile } from '../../../../../lib/api/profile';
import { withQuery } from '../../../../../lib/stringUtils';
import { submitHubLead } from '../../../../../lib/api/leads';

type Action =
  | { type: 'SET_TOAST'; toast: string | undefined }
  | { type: 'SET_FEEDBACK_EXPANDED'; expanded: boolean }
  | { type: 'SET_FEEDBACK_QUESTION'; question: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'LOAD_SUBSCRIPTIONS_PENDING' }
  | { type: 'LOAD_SUBSCRIPTIONS_RESOLVED'; data: Subscription }
  | { type: 'LOAD_SUBSCRIPTIONS_REJECTED'; err: Error }
  | { type: 'SAVE_SUBSCRIPTIONS_PENDING' }
  | { type: 'SAVE_SUBSCRIPTIONS_RESOLVED'; data: Subscription }
  | { type: 'SAVE_SUBSCRIPTIONS_REJECTED'; err: Error }
  | { type: 'SUBMIT_FEEDBACK_PENDING' }
  | { type: 'SUBMIT_FEEDBACK_RESOLVED'; data: any }
  | { type: 'SUBMIT_FEEDBACK_REJECTED'; err: Error };

interface State {
  subscriptions?: Subscription;
  isLoggedIn;
  isLoading: boolean;
  isSaving: boolean;
  feedbackQuestion: string;
  isFeedbackSubmitting: boolean;
  hasSubmittedFeedback: boolean;
  toast?: string;
}

const initialState: State = {
  isLoggedIn: false,
  isLoading: true,
  isSaving: false,
  feedbackQuestion: '',
  isFeedbackSubmitting: false,
  hasSubmittedFeedback: false,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_FEEDBACK_QUESTION':
      return {
        ...state,

        feedbackQuestion: action.question,
      };

    case 'SET_TOAST':
      return {
        ...state,

        toast: action.toast,
      };

    case 'LOAD_SUBSCRIPTIONS_PENDING':
      return {
        ...state,

        isLoggedIn: true,
        isLoading: true,
      };

    case 'LOAD_SUBSCRIPTIONS_RESOLVED':
      return {
        ...state,

        isLoading: false,
        subscriptions: action.data,
      };

    case 'LOAD_SUBSCRIPTIONS_REJECTED':
      return {
        ...state,

        isLoading: false,
      };

    case 'SAVE_SUBSCRIPTIONS_PENDING':
      return {
        ...state,

        isSaving: true,
      };

    case 'SAVE_SUBSCRIPTIONS_RESOLVED':
      return {
        ...state,

        isSaving: false,
        subscriptions: action.data,
        toast: 'Subscription saved.',
      };

    case 'SAVE_SUBSCRIPTIONS_REJECTED':
      return {
        ...state,

        isSaving: false,
        toast: 'There was an error.',
      };

    case 'SUBMIT_FEEDBACK_PENDING':
      return {
        ...state,

        isFeedbackSubmitting: true,
      };

    case 'SUBMIT_FEEDBACK_RESOLVED':
      return {
        ...state,

        isFeedbackSubmitting: false,
        hasSubmittedFeedback: true,
        toast: 'Question submitted.',
      };

    case 'SUBMIT_FEEDBACK_REJECTED':
      return {
        ...state,

        isFeedbackSubmitting: false,
        toast: 'There was an error.',
      };

    case 'SET_LOADING':
      return {
        ...state,

        isLoading: action.loading,
      };

    default:
      return state;
  }
};

const createDispatchers = (dispatch: Dispatch<Action>) => ({
  clearToast: () => dispatch({ type: 'SET_TOAST', toast: undefined }),
  setFeedbackQuestion: (question: string) =>
    dispatch({ type: 'SET_FEEDBACK_QUESTION', question }),
  loadData: async () => {
    if (!hasAccountToken()) {
      return dispatch({ type: 'SET_LOADING', loading: false });
    }

    dispatch({ type: 'LOAD_SUBSCRIPTIONS_PENDING' });

    try {
      const profile = await getProfile();

      dispatch({
        type: 'LOAD_SUBSCRIPTIONS_RESOLVED',
        data: await getSubscriptions(profile.profile.email as string),
      });
    } catch (err) {
      dispatch({ type: 'LOAD_SUBSCRIPTIONS_REJECTED', err });
    }
  },

  submitFeedback: createPromiseDispatcher(
    dispatch,
    'SUBMIT_FEEDBACK',
    submitHubLead
  ),
  saveSubscriptions: async ({
    email,
    subscriptions,
    unsubscribed,
  }: Subscription) => {
    dispatch({ type: 'SAVE_SUBSCRIPTIONS_PENDING' });

    try {
      dispatch({
        type: 'SAVE_SUBSCRIPTIONS_RESOLVED',
        data: await saveSubscriptions(
          email as string,
          subscriptions,
          unsubscribed
        ),
      });
    } catch (err) {
      dispatch({ type: 'SAVE_SUBSCRIPTIONS_REJECTED', err });
    }
  },
});

interface Actions {
  toggleSubscription: (list: string) => any;
}

export default (): [State, ReturnType<typeof createDispatchers> & Actions] => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { subscriptions } = state;
  const dispatchers = createDispatchers(dispatch);

  useMountEffect(() => {
    dispatchers.loadData();
  });

  return [
    state,
    {
      ...dispatchers,

      toggleSubscription: (list: string) => {
        if (!hasAccountToken() || !subscriptions) {
          return window.open(
            withQuery('/authenticate', {
              redirect: location.href,
            }),
            '_self'
          );
        }

        return dispatchers.saveSubscriptions({
          ...subscriptions,

          subscriptions: {
            ...subscriptions.subscriptions,

            [list]: !subscriptions.subscriptions[list],
          },
        });
      },
    },
  ];
};
